import { createAppAsyncThunk } from "@/store/thunks/createAppAsyncThunk";
import { addFileToHeliaThunk } from '@/store/thunks/poc/helia';

// No global withLoader: the upload's lifecycle outlives this thunk. The thunk
// resolves at the backend handoff, but the upload is only complete once the
// server peer finishes cloning (uploadSuccessful). Progress and completion are
// tracked per-entry in heliaSlice.uploadProgress and surfaced by the upload
// modal, which persists until `stage === 'complete'`.
export const uploadFileThunk = createAppAsyncThunk(
  "sixDetail/uploadFile",
  async(
    { sixId, file, uploadId: providedUploadId }: { sixId: string; file: File; uploadId?: string },
    { dispatch, getState, extra: { loggerService } }
  ) => {
    const log = loggerService.createLogger({ namespace: 'six3ro:upload:uploadFileThunk' });
    const uploadId = providedUploadId ?? crypto.randomUUID();
    const state = getState().sixDetail;
    const participants = state.participants;

    log.info('uploadFileThunk started', { sixId, uploadId, fileName: file.name });

    // Recipient coverage depends entirely on the participant list. If it is
    // not loaded yet, proceeding would encrypt the file for an incomplete
    // recipient set — block the upload rather than ship an under-shared file.
    if (participants === undefined) {
      log.error('uploadFileThunk: participants undefined, aborting', { sixId, uploadId });
      throw new Error(
        'SIX participants are still loading. Please wait a moment and try again.'
      );
    }

    const normalizedFolderPath =
      state.currentFolderPath !== undefined && state.currentFolderPath !== '/'
        ? state.currentFolderPath.replace(/\/+$/, '')
        : '';
    const path = normalizedFolderPath.length > 0
      ? `${normalizedFolderPath}/${file.name}`
      : `/${file.name}`;

    const allParticipantIds = [
      ...participants.owners,
      ...participants.internalParticipants,
      ...participants.externalParticipants,
    ].map((participant) => participant.id);

    log.info('dispatching addFileToHeliaThunk', { sixId, uploadId, path, participantCount: allParticipantIds.length });

    try {
      const result = await dispatch(
        addFileToHeliaThunk({
          sixId,
          allParticipantIds,
          file,
          path,
          uploadId,
        })
      ).unwrap();

      log.info('addFileToHeliaThunk resolved (handoff complete; upload awaits server replication)', { sixId, uploadId: result.uploadId });
      return { uploadId: result.uploadId };
    } catch (error) {
      log.error('addFileToHeliaThunk threw', {
        sixId,
        uploadId,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      throw error;
    }
  }
);
